import React, { useState, useEffect } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Skeleton } from "../ui/skeleton";
import { CreditCardIcon, PlusIcon, TrashIcon } from "lucide-react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import AddCardComponent from "./AddCardComponent";
import { BillingService } from "../../services/BillingService";
import { usePermissions } from "../../PermissionsProvider";
import { PaymentMethod } from "../../model_gen/billing";
import { cn } from "../../lib/utils";

// Load Stripe outside of the component to avoid recreating it on every render
const stripePromise = loadStripe("pk_live_51PfPaKIWtETaTLIE0mkYSP0hAmlpoXGA11dgiBI4QmIiSLE4RSEB7UuYPIH1lES7Tts7CYVcbc4qS24UIer30NgH003W4uNlpZ");

const BillingPaymentMethods: React.FC = () => {
  const { apiKey } = usePermissions();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    setLoading(true);
    BillingService.getPaymentMethods(setLoading, apiKey).subscribe((paymentMethods: PaymentMethod[]) => {
      // Sort payment methods by date_created descending and put the default method first
      const sortedMethods = paymentMethods.sort((a, b) => {
        if (a.is_default) return -1;
        if (b.is_default) return 1;
        return new Date(b.date_created).getTime() - new Date(a.date_created).getTime();
      });
      setPaymentMethods(sortedMethods);
    }, (error: any) => {
      console.error("Error fetching payment methods:", error);
      setLoading(false);
    });
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleSetDefault = (methodId: number) => {
    setLoading(true);
    BillingService.setPaymentMethodDefault(methodId, setLoading, apiKey).subscribe(
      () => {
        // Wait 2s before fetching payment methods
        setTimeout(() => {
          fetchPaymentMethods();
        }, 2000);
      },
      (error: any) => {
        console.error("Error setting payment method as default:", error);
        setLoading(false);
      }
    );
  };

  const handleDelete = (methodId: number) => {
    setLoading(true);
    BillingService.deletePaymentMethod(methodId, setLoading, apiKey).subscribe(
      () => {
        fetchPaymentMethods();
      },
      (error: any) => {
        console.error("Error deleting payment method:", error);
        setLoading(false);
      }
    );
  };

  const handlePaymentMethodAdded = () => {
    setIsModalVisible(false);
    fetchPaymentMethods();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-gc-med text-foreground">Payment Methods</h3>
          <p className="text-sm text-muted-foreground">Manage your payment methods and billing information</p>
        </div>
        <Button onClick={showModal} className="gap-2">
          <PlusIcon className="w-4 h-4" />
          Add Payment Method
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="border-border">
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-5 w-5 rounded" />
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-3 w-20" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <Skeleton className="h-6 w-16 rounded-full" />
                    <Skeleton className="h-8 w-16 rounded" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : paymentMethods.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {paymentMethods.map((method) => (
            <Card key={method.id} className="border-border">
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <CreditCardIcon className="w-5 h-5 text-maitai-green" />
                    <div>
                      <p className="text-sm font-gc-med text-foreground">
                        •••• {method.meta?.card?.last4}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Expires {method.meta?.card?.exp_month?.toString().padStart(2, "0")}/
                        {method.meta?.card?.exp_year?.toString().slice(-2)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    {method.is_default ? (
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                        Default
                      </span>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSetDefault(method.id)}
                        disabled={loading}
                        className="text-xs"
                      >
                        Set as default
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(method.id)}
                      disabled={loading}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <TrashIcon className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="space-y-3">
            <CreditCardIcon className="w-12 h-12 text-muted-foreground mx-auto" />
            <h3 className="text-lg font-gc-med text-foreground">
              No payment methods found
            </h3>
            <p className="text-muted-foreground max-w-md">
              Add a payment method to start using Maitai services.
            </p>
            <Button onClick={showModal} className="gap-2 mt-4">
              <PlusIcon className="w-4 h-4" />
              Add Payment Method
            </Button>
          </div>
        </div>
      )}

      <Dialog open={isModalVisible} onOpenChange={setIsModalVisible}>
        <DialogContent className="max-w-md bg-background border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground">
              <CreditCardIcon className="w-5 h-5 text-maitai-green" />
              Add Payment Method
            </DialogTitle>
          </DialogHeader>
          <Elements stripe={stripePromise}>
            <AddCardComponent onPaymentMethodAdded={handlePaymentMethodAdded} />
          </Elements>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BillingPaymentMethods;
